import React, { Fragment, useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { toast } from 'react-toastify';
import type { IClientUser } from '../../interfaces/user';
import Navigation from '../../components/Navigation';
import { GET_UNDELIVERED_EMAILS } from '../../graphql/adminConfig';

import mailIcon from '../../assets/svg/mail.svg';

const AdminUndeliveredEmailsPage: React.FC<IClientUser> = (props) => {
    const client = useApolloClient();
    const [currentPage, setCurrentPage] = useState(1);
    const [isExporting, setIsExporting] = useState(false);
    const usersPerPage = 10;

    const { data, loading, error } = useQuery(GET_UNDELIVERED_EMAILS, {
        variables: { limit: usersPerPage, page: currentPage },
        fetchPolicy: 'network-only' // always fetch latest
    });

    const undeliveredData = data?.undeliveredEmails;
    const emails = undeliveredData?.emails || [];
    const totalCount = undeliveredData?.count || 0;
    const totalPages = Math.ceil(totalCount / usersPerPage);
    const startIndex = (currentPage - 1) * usersPerPage;

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(Number(dateString) || dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleExportCSV = async () => {
        setIsExporting(true);
        try {
            const { data: exportData } = await client.query({
                query: GET_UNDELIVERED_EMAILS,
                variables: { limit: 0, page: 1 },
                fetchPolicy: 'network-only'
            });

            const allEmails = exportData?.undeliveredEmails?.emails || [];
            if (allEmails.length === 0) {
                toast.info('No emails to export.');
                setIsExporting(false);
                return;
            }

            const headers = ['Date/Time', 'Recipient', 'Subject', 'Error Reason'];
            const rows = allEmails.map((email: any) => [
                formatDate(email.createdAt),
                email.email,
                email.subject || 'No Subject',
                email.errorMessage || 'Unknown Error'
            ]);

            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const dateStr = new Date().toISOString().split('T')[0];
            link.setAttribute('download', `undelivered-emails-${dateStr}.csv`);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Export completed successfully!');
        } catch (err: any) {
            toast.error('Failed to export CSV. Please try again later.');
        } finally {
            setIsExporting(false);
        }
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const prevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const nextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                endPage = 4;
            }

            if (currentPage >= totalPages - 2) {
                startPage = totalPages - 3;
            }

            if (startPage > 2) {
                pages.push(-1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages - 1) {
                pages.push(-2);
            }

            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <main className="page-admin-users">
            <Fragment>
                <Navigation {...props} />
                <section className="container">
                    <div className="admin-users-container">

                        {/* Header Section */}
                        <div className="admin-header">
                            <div className="admin-header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                <div>
                                    <h1 className="admin-title">Undelivered Emails</h1>
                                    <p className="admin-subtitle">Monitor failed email deliveries</p>
                                </div>
                                <button
                                    className="export-csv-btn"
                                    onClick={handleExportCSV}
                                    disabled={isExporting || totalCount === 0}
                                    title="Export to Excel"
                                >
                                    {isExporting ? (
                                        <span className="dropdown-loading-spinner" style={{ margin: 0, width: '16px', height: '16px' }}></span>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                    )}
                                    <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                                </button>
                            </div>
                        </div>

                        {!loading && !error && (
                            <div className="admin-stats-card">
                                <div className="stat-card">
                                    <div className="stat-icon-wrapper">
                                        <img src={mailIcon} alt="Emails" />
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-label">Total Undelivered Emails</div>
                                        <div className="stat-value">{totalCount}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <div className="admin-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading emails...</p>
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="admin-error">
                                <i className="fas fa-exclamation-circle"></i>
                                <p>Error loading emails: {error.message}</p>
                            </div>
                        )}

                        {/* Table */}
                        {!loading && !error && (
                            <>
                                {emails.length > 0 ? (
                                    <>
                                        <div className="table-container">
                                            <table className="table is-fullwidth is-hoverable">
                                                <thead>
                                                    <tr>
                                                        <th className="table-number-col">#</th>
                                                        <th>Date/Time</th>
                                                        <th>Recipient</th>
                                                        <th>Subject</th>
                                                        <th>Error Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {emails.map((email: any, index: number) => (
                                                        <tr key={email.id || index}>
                                                            <td className="table-number-col">{startIndex + index + 1}</td>
                                                            <td>{formatDate(email.createdAt)}</td>
                                                            <td className="table-email-col">{email.email}</td>
                                                            <td>
                                                                {email.subject ? (
                                                                    email.subject
                                                                ) : (
                                                                    <span className="has-text-grey-light is-italic">No Subject</span>
                                                                )}
                                                            </td>
                                                            <td className="has-text-danger">
                                                                {email.errorMessage ? (
                                                                    email.errorMessage
                                                                ) : (
                                                                    <span className="has-text-grey-light is-italic">Unknown Error</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination Info and Controls */}
                                        <div className="pagination-wrapper">
                                            <div className="pagination-info">
                                                Showing {startIndex + 1}-{Math.min(startIndex + emails.length, totalCount)} of {totalCount} emails
                                            </div>

                                            {totalPages > 1 && (
                                                <div className="pagination-controls">
                                                    <button
                                                        className="pagination-btn"
                                                        onClick={prevPage}
                                                        disabled={currentPage === 1}
                                                    >
                                                        <i className="fas fa-chevron-left"></i>
                                                        Previous
                                                    </button>

                                                    <div className="pagination-numbers">
                                                        {getPageNumbers().map((page, index) => {
                                                            if (page < 0) {
                                                                return (
                                                                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                                                                        ...
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <button
                                                                    key={page}
                                                                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                                                    onClick={() => goToPage(page)}
                                                                >
                                                                    {page}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <button
                                                        className="pagination-btn"
                                                        onClick={nextPage}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                        <i className="fas fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="admin-empty">
                                        <i className="fas fa-envelope-open-text"></i>
                                        <p className="empty-title">No undelivered emails found</p>
                                        <p className="empty-subtitle">
                                            All emails are delivering successfully.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>
            </Fragment>
        </main>
    );
};

export default AdminUndeliveredEmailsPage;
